import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
  IconButton,
  Button,
  Paper
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';

// Bid details interface for the header information
interface BidDetails {
  contractNo: string;
  title: string;
  categories: string;
}

interface BidResultsDetailsProps {
  bidId: string;
  bidDetails: BidDetails;
  onBack: () => void;
}

const BidResultsDetails: React.FC<BidResultsDetailsProps> = ({
  bidId,
  bidDetails,
  onBack
}) => {
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  // File validation function
  const validateFile = (file: File): string | null => {
    // Check file type
    const allowedExtensions = [
      '.pdf',
      '.xls',
      '.xlsx',
      '.doc',
      '.docx',
      '.zip'
    ];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf('.'))
      .toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      return `Invalid file type. Only ${allowedExtensions.join(
        ', '
      )} files are allowed.`;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      return 'File is too large. Maximum file size is 10MB.';
    }

    return null;
  };

  // Handle drag events
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle file drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      const validationError = validateFile(file);

      if (validationError) {
        setFileError(validationError);
        toast.error(validationError);
        setTimeout(() => setFileError(null), 5000);
      } else {
        setSelectedFile(file);
        setFileError(null);
      }
    }
  };

  // Function to handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];
      if (file) {
        const validationError = validateFile(file);

        if (validationError) {
          setFileError(validationError);
          toast.error(validationError);
          setTimeout(() => setFileError(null), 5000);
        } else {
          setSelectedFile(file);
          setFileError(null);
        }
      }
    }
  };

  // Function to clear the form
  const handleClear = () => {
    setSelectedFile(null);
    setFileError(null);
  };

  // Function to handle file upload
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first.');
      return;
    }

    setIsLoading(true);

    // Create FormData for the file
    const uploadFormData = new FormData();
    uploadFormData.append('UploadBidsDocument', selectedFile);
    uploadFormData.append('DocumentDescription', 'Bid Result Document');

    try {
      // Upload the document
      const response = await httpRequest(
        'POST',
        '/bid_portal/bids/upload_document',
        uploadFormData,
        { 'Content-Type': 'multipart/form-data' }
      );

      if (response.type === 'NETWORK_ERROR' || response.status !== 'success') {
        throw new Error(
          response.message || `Failed to upload document: ${selectedFile.name}`
        );
      }

      const payload = {
        BidID: bidId || bidDetails.contractNo,
        BidResultDocument: {
          FileName: response.data?.DocumentName,
          Type: response?.data?.FileType
        }
      };

      // Upload the result
      const resultResponse = await httpRequest(
        'POST',
        '/bid_portal/bid_details/upload_bid_result',
        payload
      );

      if (resultResponse?.status === 'success') {
        toast.success('File uploaded successfully!');
        // Clear selected file and reset loading state after successful upload
        handleClear();
      } else {
        toast.error(resultResponse.message || 'File result upload failed.');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        background: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh'
      }}
    >
      {/* Header with back button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
          gap: 2
        }}
      >
        <IconButton
          onClick={onBack}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.2)
            }
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 80,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          File Upload
        </Typography>
      </Box>

      {/* Bid Information Card */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.5
                }}
              >
                Contract No.: {bidDetails.contractNo}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.5
                }}
              >
                Title: {bidDetails.title}
              </Typography>
            </Box>

            {/* Drag & Drop Zone */}
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 1
                }}
              >
                File: <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <Paper
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                elevation={0}
                sx={{
                  p: 3,
                  border: dragActive
                    ? '2px dashed #1976d2'
                    : fileError
                    ? '2px dashed #d32f2f'
                    : selectedFile
                    ? '2px dashed #2e7d32'
                    : '2px dashed #ccc',
                  backgroundColor: dragActive
                    ? 'rgba(25, 118, 210, 0.08)'
                    : 'transparent',
                  borderRadius: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                <input
                  type="file"
                  hidden
                  id="bid-result-file-input"
                  accept=".pdf,.xls,.xlsx,.doc,.docx,.zip"
                  onChange={handleFileChange}
                />

                <label
                  htmlFor="bid-result-file-input"
                  style={{ cursor: 'pointer', display: 'block' }}
                >
                  <CloudUploadIcon
                    sx={{
                      fontSize: 48,
                      color: fileError
                        ? 'error.main'
                        : selectedFile
                        ? 'success.main'
                        : 'primary.main',
                      mb: 1
                    }}
                  />
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    sx={{ fontWeight: 500, mb: 0.5 }}
                  >
                    {selectedFile
                      ? 'Drop file to replace or click to browse'
                      : 'Drag & drop bid result file here or click to browse'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Supported formats: .pdf, .xls, .xlsx, .doc, .docx, .zip
                    (Max: 10MB)
                  </Typography>
                </label>

                {selectedFile && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'success.main',
                        fontWeight: 'medium',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1
                      }}
                    >
                      ✅ Selected file: {selectedFile.name}
                    </Typography>
                  </Box>
                )}

                {fileError && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'error.main',
                        fontWeight: 'medium'
                      }}
                    >
                      ❌ {fileError}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>

          {/* Upload and Clear Buttons */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={isLoading || !selectedFile}
              sx={{
                borderRadius: 1.5,
                px: 3,
                py: 1,
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.9)
                }
              }}
            >
              {isLoading ? 'Uploading...' : 'Upload'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClear}
              disabled={isLoading}
              sx={{
                borderRadius: 1.5,
                px: 3,
                py: 1,
                fontWeight: 'bold'
              }}
            >
              Clear
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BidResultsDetails;
